import { useState, useEffect, useCallback } from 'react'

/**
 * Observes an element's own inline width. Returns a callback ref to attach,
 * the measured width, and whether a measurement has happened yet.
 *
 * Uses a callback ref (not a RefObject) so the observer re-attaches when the
 * consumer conditionally mounts/unmounts the observed element.
 */
export function useContainerWidth<T extends HTMLElement = HTMLElement> (): {
	ref: (node: T | null) => void
	width: number
	measured: boolean
} {
	const [node, setNode] = useState<T | null>(null)
	const [width, setWidth] = useState(0)
	const [measured, setMeasured] = useState(false)

	const ref = useCallback((el: T | null) => { setNode(el) }, [])

	useEffect(() => {
		if (!node) return

		let rafId = 0
		const measure = () => {
			setWidth(node.offsetWidth)
			setMeasured(true)
		}

		const ro = new ResizeObserver(() => {
			cancelAnimationFrame(rafId)
			rafId = requestAnimationFrame(measure)
		})
		ro.observe(node)
		rafId = requestAnimationFrame(measure)

		return () => {
			ro.disconnect()
			cancelAnimationFrame(rafId)
		}
	}, [node])

	return { ref, width, measured }
}
